import type { AddFundsPayload } from "@/features/payments/hooks/usePayments";
import { convertToBytes } from "@/lib/utils";
import { billingTransactionTypes, pricing } from "@yedalabs/schema";
import {
    addYears,
    getDate,
    getMonth,
    getYear,
    isBefore,
    isSameDay,
    setDate,
    setYear,
    startOfDay,
} from "date-fns";

export const SIGNUP_BONUS = {
    storage: convertToBytes(1, "GB"),
    bandwidth: convertToBytes(1, "GB"),
    credits: 10,
} as const;

export const applyDiscount = (
    amount: number,
    discountPercent: number
): number => {
    const clamped = Math.min(100, Math.max(0, discountPercent));
    return amount * (1 - clamped / 100);
};

export const MIN_PAYMENT_AMOUNT = 100;
export const MIN_PAYMENT_AMOUNT_ADMIN = 10;

export const getMinPaymentAmount = (isAdmin?: boolean): number =>
    isAdmin ? MIN_PAYMENT_AMOUNT_ADMIN : MIN_PAYMENT_AMOUNT;

export const VAT_PERCENT = 18;

export const applyVat = (amount: number): number =>
    amount * (1 + VAT_PERCENT / 100);

export const calculateTotal = (payload: AddFundsPayload): number =>
    applyVat(
        billingTransactionTypes.reduce((sum, type) => {
            const { amount, discount } = payload[type];
            return sum + applyDiscount(pricing[type] * amount, discount);
        }, 0)
    );

export const formatPrice = (value: number): string =>
    new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value) + "₪";

/** Returns today as the billing cycle start date. Adjusts Feb 29 → Feb 28 so the date repeats every year. */
export const getBillingCycleStartDate = (): Date => {
    const today = startOfDay(new Date());
    if (getMonth(today) === 1 && getDate(today) === 29) {
        return setDate(today, 28);
    }
    return today;
};

/** Returns the next yearly anniversary of the billing cycle start date, or null if no start date. */
export const getNextBillingCycleDate = (
    start: Date | null | undefined
): Date | null => {
    if (!start) return null;
    const today = startOfDay(new Date());
    const firstAnniversary = addYears(startOfDay(start), 1);
    if (isBefore(today, firstAnniversary)) return firstAnniversary;
    const thisYear = setYear(start, getYear(today));
    return isBefore(today, thisYear) ? thisYear : addYears(thisYear, 1);
};

/** Returns the next N future billing cycle anniversary dates, or an empty array if no start date. */
export const getNextNBillingCycleDates = (
    start: Date | null | undefined,
    count: number
): Date[] => {
    const first = getNextBillingCycleDate(start);
    if (!first) return [];
    return Array.from({ length: count }, (_, i) => addYears(first, i));
};

/** Returns true if today is the billing cycle anniversary (same month+day) but not the original start date itself. */
export const shouldResetBillingCycleToday = (
    billingCycleStartDate: Date | null | undefined
): boolean => {
    if (!billingCycleStartDate) return false;
    const today = startOfDay(new Date());
    const start = startOfDay(billingCycleStartDate);
    return (
        getMonth(today) === getMonth(start) &&
        getDate(today) === getDate(start) &&
        !isSameDay(today, start)
    );
};
